import { Injectable } from '@angular/core';
import { ICardSettings } from 'src/app/interface/ICardSettings.interface';
import { IRepurchase, IRepurchaseListResponse } from 'src/app/interface/IRepurchase.interface';
import { RepurchaseModel } from 'src/app/model/repurchase.model';
import { ApiHappylandService } from '../api-happyland/api-happyland.service';
import { Subject, Observable } from 'rxjs';

@Injectable({
  providedIn: 'root',
})
export class CardSettingsService {
  // Subject para notificar cambios en la configuración de la tarjeta
  private cardSettingsChangedSubject = new Subject<ICardSettings>();

  // Observable público para que los componentes se suscriban a cambios
  cardSettingsChanged$: Observable<ICardSettings> = this.cardSettingsChangedSubject.asObservable();

  constructor(private happylandService: ApiHappylandService) {}

  private buildCacheKey(account: number): string {
    return `cardSettings:${account}`;
  }

  getCachedCardSettings(account: number): ICardSettings | null {
    try {
      const raw = localStorage.getItem(this.buildCacheKey(account));
      return raw ? (JSON.parse(raw) as ICardSettings) : null;
    } catch {
      return null;
    }
  }

  setCachedCardSettings(settings: ICardSettings): void {
    try {
      localStorage.setItem(this.buildCacheKey(settings.account), JSON.stringify(settings));
    } catch {
      // Ignorar errores de almacenamiento
    }
  }

  clearCachedCardSettings(account: number): void {
    try {
      localStorage.removeItem(this.buildCacheKey(account));
    } catch {
      // Ignorar errores de almacenamiento
    }
  }

  /**
   * Obtener la configuración personalizada del usuario
   * @param account Número de cuenta/tarjeta
   * @returns Configuración de la tarjeta
   */
  async getLatestCardSettings(account: number): Promise<ICardSettings | null> {
    try {
      const response = await this.happylandService.getCardSettings(account);

      if (response.status !== 200 || !response.body.data) {
        return null;
      }

      const cardSettings: ICardSettings = {
        account: response.body.data.account,
        showSpecialOffer: response.body.data.showSpecialOffer,
        segmentRef: response.body.data.segmentRef,
      };

      this.setCachedCardSettings(cardSettings);
      return Promise.resolve(cardSettings);
    } catch (errCritical) {
      // console.error(errCritical);
      return Promise.reject(errCritical);
    }
  }

  /**
   * Crea una nueva configuración con el fin de personalizar la experiencia del usuario dentro de la web
   * @param settings Configuración a crear
   * @returns Configuración creada
   */
  async createNewSettings(settings: ICardSettings): Promise<ICardSettings> {
    try {
      const response = await this.happylandService.createCardSettings(settings);

      if (response.status === 200 || response.status === 201) {
        const createdSettings: ICardSettings = {
          account: response.body.data.account,
          showSpecialOffer: response.body.data.showSpecialOffer,
          segmentRef: response.body.data.segmentRef,
        };
        this.setCachedCardSettings(createdSettings);
        return Promise.resolve(createdSettings);
      }

      return Promise.reject(new Error('Failed to create card settings'));
    } catch (errCritical) {
      // console.error(errCritical);
      return Promise.reject(errCritical);
    }
  }

  /**
   * Permite actualizar la experiencia del usuario dentro de la aplicación
   * @param settings Configuración a actualizar
   * @returns Configuración actualizada
   */
  async updateCardSettings(settings: ICardSettings): Promise<ICardSettings> {
    try {
      const response = await this.happylandService.updateCardSettings(settings.account, settings.showSpecialOffer ?? false);

      if (response.status === 200 || response.status === 201) {
        const updatedSettings: ICardSettings = {
          account: response.body.data.account,
          showSpecialOffer: response.body.data.showSpecialOffer,
          segmentRef: response.body.data.segmentRef,
        };
        this.setCachedCardSettings(updatedSettings);
        this.cardSettingsChangedSubject.next(updatedSettings);
        return Promise.resolve(updatedSettings);
      }

      return Promise.reject(new Error('Failed to update card settings'));
    } catch (errCritical) {
      // console.error(errCritical);
      return Promise.reject(errCritical);
    }
  }

  /**
   * Actualiza la configuración de la tarjeta usando REST API
   * @param account Número de cuenta/tarjeta
   * @param showSpecialOffer Indica si se deben mostrar ofertas segmentadas
   * @returns Configuración actualizada
   */
  async updateCardSettingsREST(account: number, showSpecialOffer: boolean): Promise<ICardSettings> {
    try {
      const response = await this.happylandService.updateCardSettings(account, showSpecialOffer);

      if (response.status === 200 || response.status === 201) {
        const updatedSettings: ICardSettings = {
          account: response.body.data.account,
          showSpecialOffer: response.body.data.showSpecialOffer,
          segmentRef: response.body.data.segmentRef,
        };
        this.setCachedCardSettings(updatedSettings);
        // Notificar a todos los suscriptores del cambio
        this.cardSettingsChangedSubject.next(updatedSettings);
        return Promise.resolve(updatedSettings);
      }

      return Promise.reject(new Error('Failed to update card settings'));
    } catch (errCritical) {
      return Promise.reject(errCritical);
    }
  }

  /**
   * Obtiene la información de la última recompra registrada.
   * @returns Información de la recompra o null si no existe
   */
  async getRepurchaseLatestInformation(): Promise<RepurchaseModel | null> {
    try {
      const response = await this.happylandService.listRepurchases({ isLatest: { eq: true } });

      if (response.status !== 200) {
        return null;
      }

      const listOfRepurchase: IRepurchaseListResponse = response.body.data;

      if (listOfRepurchase.items?.[0]) {
        return new RepurchaseModel(listOfRepurchase.items[0]);
      }

      return null;
    } catch (error) {
      return Promise.reject(error);
    }
  }
}
